import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl'
import { reduxForm } from 'redux-form'
import { FieldError, ErrorField, BirthdateField, CharField,
  RadioField, CheckboxField, CountrySelector } from 'components/forms'
import { Button } from 'components/util'
import * as authActions from 'services/auth/actions'
import * as consentActions from 'services/consents/actions'
import * as auth from 'services/auth/selectors'
import * as site from 'services/site/selectors'
import * as consents from 'services/consents/selectors'
import styles from './_signup.css'
import validate from './validate'
import ErrorComponent from '../ErrorComponent'
import messages from '../messages'
import { changeBulk } from 'shared/actions'
import ReCAPTCHA from 'react-google-recaptcha'
import { selectConsentDetailsForIsoCode } from 'shared/consentsUtil'

export const FORM_NAME = 'signup'
let captcha

const formOptions = {
  form: FORM_NAME,
  validate,
}

const mapStateToProps = (state) => ({
  country: auth.selectAuthCountry(state),
  siteName: site.selectSiteName(state),
  onboarding: auth.selectOnboarding(state),
  initialFormValues: auth.selectOnboardingInitialFormValues(state),
  isDev: site.selectDev(state),
  consentMatrix: consents.selectConsentMatrix(state),
  reCaptchaResponse: auth.selectRecaptchaResponse(state),
})

const mapDispatchToActions = {
  ...authActions,
  ...consentActions,
  changeBulk,
}

const CODE_US = 'US' // Country or Standard

export class SignupForm extends Component {

  static propTypes = {
    intl: PropTypes.object,
    invalid: PropTypes.bool,
    country: PropTypes.string,
    siteName: PropTypes.string,
    onboarding: PropTypes.string,
    signingUp: PropTypes.bool,
    initialFormValues: PropTypes.object,
    onClickSignup: PropTypes.func,
    onClickForgotPassword: PropTypes.func,
    onLoginSuccess: PropTypes.func,
    handleSubmit: PropTypes.func,
    fetchGeoIp: PropTypes.func,
    changeBulk: PropTypes.func,
    error: PropTypes.string,
    isDev: PropTypes.bool,
    consentMatrix: PropTypes.object,
    fetchConsentMatrix: PropTypes.func,
    gdpr_iso_code: PropTypes.string,
    showSpinner: PropTypes.func,
    reCaptchaResponse: PropTypes.string,
  }

  state = {
    privacyStandard: CODE_US,
  }

  componentWillMount () {
    this.props.fetchGeoIp()
      .then(() => {
        const { country, onboarding, initialFormValues } = this.props
        let initialData = { promotionalPreference: this.isUS(country),
          gdpr_iso_code: (country || CODE_US).toUpperCase() }
        this.handleCountryChange(country)
        // merge in onboarding data if onboarding
        if (onboarding) {
          initialData = { ...initialData, ...initialFormValues }
        }

        this.props.changeBulk(FORM_NAME, initialData)
      })
  }

  componentWillReceiveProps (nextProps) {
    if ('onboarding' in nextProps && nextProps.onboarding !== this.props.onboarding) {
      this.props.changeBulk(FORM_NAME, nextProps.initialFormValues)
    }
  }

  componentDidUpdate () {
    if (this.props.error) {
      captcha.reset()
    }
  }

  handleCountryChange = (country) => {
    this.props.showSpinner(true)
    this.props.fetchConsentMatrix(country)
      .then(() => {
        const consentDetails = selectConsentDetailsForIsoCode(this.props.consentMatrix, country)
        const { standard } = consentDetails
        this.setState({
          privacyStandard: standard,
        })
        if (this.isUS(standard)) {
          this.props.changeBulk(FORM_NAME, { terms: true })
        }
        this.props.showSpinner(false)
      })
  }

  isUS (code) {
    return (code && code.toUpperCase()) === CODE_US
  }

  handleAutofillNewUser = () => {
    this.props.changeBulk(FORM_NAME, {
      firstName: 'Autogenerated',
      lastName: 'Autogenerated',
      email: `autotestmmf_${Date.now()}@gmail.com`,
      password: 'password',
      day: '26',
      month: '12',
      year: '1981',
      gdpr_iso_code: 'US',
      gender: 'M',
      terms: true,
      promotionalPreference: false,
    })
  }

  render () {
    const {
      handleSubmit,
      intl,
      siteName,
      gdpr_iso_code,
      signingUp,
      onboarding,
      error,
      isDev,
      invalid,
      reCaptchaResponse,
    } = this.props
    const privacyStandard = this.state.privacyStandard

    return (
      <form>

        <ReCAPTCHA
          ref={(el) => { captcha = el }}
          size="invisible"
          sitekey="6LcxilMUAAAAAO58qmA-RhNUA2OWX_UbTPWCireM" // TODO WWW-2109
          onChange={
            (value) => {
              this.props.changeBulk(FORM_NAME, {
                'g-recaptcha-response': value,
              })
              handleSubmit()
            }
          }
        />

        {isDev ?
          <Button
            type='danger'
            onClick={this.handleAutofillNewUser}
            className={styles.autofillButton}
          >
            <FormattedMessage
              id='auth.signup.autofill'
              defaultMessage='Autofill New User'
            />
          </Button>
        : null}

        <CharField
          slim
          maxLength={30}
          name='firstName'
          placeholder={intl.formatMessage(messages.firstName)}
          labelComponent={null}
          normalize={(value) => value.trim()}
          disabled={signingUp}
          errorComponent={(
            <ErrorComponent name={intl.formatMessage(messages.firstName)} />
          )}
        />
        <CharField
          slim
          maxLength={30}
          name='lastName'
          placeholder={intl.formatMessage(messages.lastName)}
          labelComponent={null}
          normalize={(value) => value.trim()}
          disabled={signingUp}
          errorComponent={(
            <ErrorComponent name={intl.formatMessage(messages.lastName)} />
          )}
        />
        <CharField
          slim
          name='email'
          placeholder={intl.formatMessage(messages.email)}
          normalize={(value) => value.trim()}
          labelComponent={null}
          disabled={signingUp}
          errorComponent={(
            <ErrorComponent name={intl.formatMessage(messages.email)} />
          )}
        />

        <CharField
          name='profileUrl'
          className={styles.honeypot}
        />

        {!onboarding ?
          <CharField
            slim
            name='password'
            type='password'
            placeholder={intl.formatMessage(messages.password)}
            labelComponent={null}
            normalize={(value) => value.trim()}
            disabled={signingUp}
            errorComponent={(
              <ErrorComponent name={intl.formatMessage(messages.password)} />
            )}
          />
        : null}

        <BirthdateField
          slim
          formName={FORM_NAME}
          name='birthdate'
          errorComponent={(
            <ErrorComponent name={intl.formatMessage(messages.birthdate)} />
          )}
        />

        <div className={styles.gender}>
          <div className={styles.male}>
            <RadioField
              slim
              button
              value='M'
              name='gender'
              icon='radio_check'
              disabled={signingUp}
              className={styles.maleLabel}
              labelComponent={(
                <FormattedMessage
                  id='auth.signup.male'
                  defaultMessage='Male'
                />
              )}
              errorComponent={null}
            />
          </div>
          <div className={styles.female}>
            <RadioField
              slim
              button
              value='F'
              name='gender'
              icon='radio_check'
              disabled={signingUp}
              className={styles.femaleLabel}
              labelComponent={(
                <FormattedMessage
                  id='auth.signup.female'
                  defaultMessage='Female'
                />
              )}
              errorComponent={null}
            />
          </div>
        </div>
        <ErrorField
          slim
          name='gender'
          labelComponent={null}
          errorComponent={(
            <ErrorComponent name={intl.formatMessage(messages.gender)} />
          )}
        />

        <CountrySelector
          slim
          name='gdpr_iso_code'
          className={styles.gdprIsoCode}
          placeholder={intl.formatMessage(messages.country)}
          onChange={this.handleCountryChange}
          required={true}
          labelComponent={null}
          errorComponent={(
            <ErrorComponent name={intl.formatMessage(messages.country)} />
          )}
        />

        <CheckboxField
          slim
          name='promotionalPreference'
          disabled={signingUp}
          className={styles.note}
          labelComponent={(
            <div>
              {gdpr_iso_code !== 'ca' ?  // eslint-disable-line camelcase
                <FormattedMessage
                  id='auth.signup.promotional_preference'
                  defaultMessage='Yes, I would like to receive by email the latest news, innovation updates, and offers from {siteName} and Under Armour.'
                  values={{ siteName }}
                />
              :
                <FormattedHTMLMessage
                  id='auth.signup.promotional_preference_ca'
                  defaultMessage={`
                    Yes, I agree to receive by email the latest news, innovation, updates, and offers from {siteName}
                    and Under Armour, Inc. You may unsubscribe at any time. See our  <a href="https://account.underarmour.com/privacy/" target="_blank" rel="noopener noreferrer" class="link">Privacy Policy</a> or <a href="mailto:customerservice@underarmour.com">Contact us</a> for
                    more information 1020 Hull Street, Baltimore MD 21230
                  `}
                  values={{ siteName }}
                />
              }
            </div>
          )}
          errorComponent={null}
        />

        {this.isUS(privacyStandard) ?
          <p className={styles.termsAndPrivacyNote}>
            <FormattedHTMLMessage
              id='auth.signup.terms_and_privacy_note'
              defaultMessage={`
                By signing up with MapMyRun, you agree to our
                <a href="https://account.underarmour.com/privacy/" target="_blank" rel="noopener noreferrer" class="link">Privacy Policy</a>
                & <a href="https://account.underarmour.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" class="link">Terms of Use.</a> 
              `}
            />
          </p>
        : <CheckboxField
          slim
          name='terms'
          className={styles.note}
          disabled={signingUp}
          labelComponent={(
            <FormattedHTMLMessage
              id='auth.signup.terms_checkbox'
              defaultMessage={`
                I have read and agree to the <a href="https://account.underarmour.com/privacy/" target="_blank" rel="noopener noreferrer" class="link">Privacy Policy</a> & <br />
                <a href="https://account.underarmour.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" class="link">Terms of Use</a>.
              `}
            />
          )}
          errorComponent={(
            <ErrorComponent name={intl.formatMessage(messages.terms)} />
          )}
        />}

        {/* server or form-wide errors, like birthdate too young */}
        {error ?
          <FieldError
            showError={!!error}
            errorMessage={error}
            className={styles.fieldError}
            errorComponent={(<ErrorComponent />)}
          />
        : null}

        <Button
          el='button'
          type='success'
          className={styles.button}
          onClick={(event) => {
            event.preventDefault()
            if (invalid || reCaptchaResponse) {
              handleSubmit()
            } else {
              captcha.execute()
            }

          }}
        >
          <FormattedMessage
            id='auth.signup.sign_up'
            defaultMessage='Sign up'
          />
        </Button>

      </form>
    )
  }

}

export const Form = reduxForm(formOptions)(SignupForm)
export const IntlForm = injectIntl(Form)
export default connect(mapStateToProps, mapDispatchToActions, null, { withRef: true })(IntlForm)



// WEBPACK FOOTER //
// ./src/components/auth/signup/SignupForm.jsx